import { dollarsToNum } from "."
import { LoanApplication } from "./loan-application"
import { LoanCalculations } from "./loan-calculations"
import { FeesByProductType, IProduct, ProductFee } from "./product"
import { IncentiveProduct } from "./types"

export type LoanOption = {
  product: IProduct
  payments: { withAutoPay: number, withoutAutoPay: number }
  apr: number
}

export type FormattedLoanOption = {
  product: IProduct
  payments: { withAutoPay: string }
  apr: string
  loanTermYears: string
  isIncentive: boolean
  loanDetailHTML?: string
}

export type GroupedLoanOptions = {
  withIncentives: (LoanOption & { matchingIncentive: IncentiveProduct })[]
  standard: LoanOption[]
}

/**
 * Product dates are Date instances on the server and ISO strings after JSON.
 * A string compared with <= against a Date coerces to NaN and always fails.
 */
function toProductDate(value: Date | string): Date {
  return new Date(value)
}

export class LoanOptions {
  static for(
    lapp: LoanApplication,
    products: IProduct[],
    feesByProductType?: FeesByProductType,
    productFees?: ProductFee[]
  ): LoanOption[] {
    return products.filter(p => LoanOptions.isEligible(lapp, p)).map(p => {
      const payments = LoanCalculations.paymentsForProduct(
        p,
        dollarsToNum(lapp.loanAmount),
        lapp.installationAddress,
        lapp.projectType,
        undefined,
        feesByProductType,
        productFees
      )

      return {
        product: p,
        payments,
        apr: LoanCalculations.apr(
          dollarsToNum(lapp.loanAmount),
          p.term,
          payments.withAutoPay
        )
      }
    }).filter(o => (
      lapp.maxAllowableMonthlyPayment > 0
        ? o.payments.withAutoPay <= lapp.maxAllowableMonthlyPayment
        : true
    )).sort((a, b) => a.payments.withAutoPay - b.payments.withAutoPay)
  }

  static isEligible(lapp: LoanApplication, p: IProduct): boolean {
    const referenceDate = lapp.createdDate ? new Date(lapp.createdDate) : new Date()
    const typeAndEquipmentMatch = (
      p.productType === lapp.projectType &&
      p.equipmentTypes.includes(lapp.equipmentType || 'standard-solar')
    )

    if (!typeAndEquipmentMatch) return false

    const effectiveDate = p.effectiveDate ? toProductDate(p.effectiveDate) : undefined
    const expirationDate = p.expirationDate ? toProductDate(p.expirationDate) : undefined
    const isEffective = !effectiveDate || effectiveDate <= referenceDate
    const isNotExpired = !expirationDate || expirationDate > referenceDate

    let isPrequalEligible = true
    if (p.prequalExpirationDate && lapp.prequalTimestamp) {
      // If product has prequal expiration check if
      // prequal was received before expiration
      isPrequalEligible = new Date(lapp.prequalTimestamp) <= toProductDate(p.prequalExpirationDate)
    }

    return isEffective && isNotExpired && isPrequalEligible
  }

  static groupLoanOptionsByIncentive(
    loanOptions: LoanOption[],
    incentiveProducts: IncentiveProduct[],
    statedGrossAnnualIncome: number,
    coStatedGrossAnnualIncome?: number
  ): GroupedLoanOptions {
    const withIncentives = []
    const standard = []

    loanOptions.forEach((loanOption) => {

      const isValidForIncentive = loanOption.product?.isValidForIncentive === true
      
      const matchingIncentive = incentiveProducts.find(
        inc => inc.loanTerm === loanOption.product?.term && isValidForIncentive
      )

      const incomeMaximum = matchingIncentive?.incentiveHouseholdIncomeMaximum
      const isPrimaryIncomeEligible =
        incomeMaximum !== undefined && statedGrossAnnualIncome <= incomeMaximum
      const isCoBorrowerIncomeEligible =
        coStatedGrossAnnualIncome === undefined ||
        (incomeMaximum !== undefined && coStatedGrossAnnualIncome <= incomeMaximum)

      const isEligible =
        matchingIncentive && isPrimaryIncomeEligible && isCoBorrowerIncomeEligible

      if (isEligible) {
        withIncentives.push({ ...loanOption, matchingIncentive })
      }

      const { apr, ...rest } = loanOption
      const formattedApr = apr ? apr.toFixed(3) : null
      const formattedWithAutoPay = rest.payments ? rest.payments.withAutoPay.toFixed(2) : null
      const formattedWithoutAutoPay = rest.payments ? rest.payments.withoutAutoPay.toFixed(2) : null

      if (!isValidForIncentive) {
        standard.push({
          ...rest,
          apr: formattedApr,
          payments: {
            withAutoPay: formattedWithAutoPay,
            withoutAutoPay: formattedWithoutAutoPay
          }
        })
      }
    })

    return { withIncentives, standard }
  }
}